import React from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl'
import moment from 'moment';

import { makeBtStyles } from '../Theme';
import Button from '../components/Button';
import { ContactEmail, ContactPhone } from '../components/ContactInfo';
import { getReturnsCenterIdFromUrl } from '../ReturnsPageApp';
import { useBrandingData } from '../dataProviders';

import ReturnShippingDescriptor from './ReturnShippingDescriptor';
import { useOrderState } from './useFindOrder';
import { useCreateReturn } from './useCreateReturn';


const useStyles = makeBtStyles( theme => ({
  root: {
    width: '780px'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px'
  },
  heading: {
    ...theme.characteristicFont,
    fontSize: '21px',
    margin: 0
  },
  orderNumber: {
    fontWeight: '500',
    fontSize: '16px'
  },
  dateHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'right',
    fontSize: '14px'
  },
  dateLabel: {
    fontWeight: '600'
  },    
  shippingDescriptor: {
  },
  button: {
    marginTop: '40px',
    fontSize: '16px'
  },
  error: {
    marginTop: '20px',
    color: '#E73434'
  },
  contactMessage: {
    backgroundColor: '#F8F8F8',
    border: '1px solid #DEDEDE',
    borderRadius: '0 0 2px 2px',
    padding: '20px'
  }
}));


export default function ConfigureReturn({ className='' }) {
  const classes = useStyles();
  const intl = useIntl();
  const { order, postal_code } = useOrderState();
  const returnsCenterId = getReturnsCenterIdFromUrl();
  const { phone, email, toggles: { show_phone, show_email } } = useBrandingData();

  const { createReturn, loading, error } = useCreateReturn();

  if( !order ) {
    return null;
  }

  const onCreate = () => order && postal_code && createReturn( returnsCenterId, {
    order_number: order.order_number,
    sales_order_id: order.sales_order_id,
    shipping_postal_code: postal_code,
    return_reason: "v0 portal return",
  });

  const pastReturnWindow = moment() > moment(order.eligible_until_timestamp);
  const contactDetails = (phone && show_phone && email && show_email) ? <>
    <ContactPhone withIcon={false} /> or <ContactEmail withIcon={false} />
  </>:( (phone && show_phone) ? (
    <ContactPhone withIcon={false} />
   ):( (email && show_email) ? (
     <ContactEmail withIcon={false} />
   ):(
     null
   )));

  return <div className={classnames(classes.root,className)}>
    <div className={classes.cardHeader}>
      <div>
        <h2 className={classes.heading}>Create Return Request</h2>
        <div className={classes.orderNumber}>Order No. {order.order_number}</div>
      </div>
      <div className={classes.dateHeader}>
        <div>
          <label className={classes.dateLabel}>Order Date:</label>
          <span>{intl.formatDate(order.order_date, { dateStyle: 'medium' })}</span>
        </div>
        <div>
          <ReturnWindowMessage eligible_until_timestamp={order.eligible_until_timestamp} />
        </div>
      </div>
    </div>

    { pastReturnWindow ? (
      (contactDetails &&
        <div className={classes.contactMessage}>
          If you have additional questions, please contact: {contactDetails}.
        </div>
      )
    ):<>
      { order.return_cost_options.map( (option,index) =>
        <ReturnShippingDescriptor costOption={option} className={classes.shippingDescriptor} key={index} />
      )}

      <Button variant="primary" className={classes.button} onClick={onCreate} disabled={loading}>
        { loading ? 'Creating Return Label' : 'Create Return Label' }
      </Button>

      { error &&
        <div className={classes.error}>
          There was a problem creating your return label.
          <div>{JSON.stringify(error)}</div>
        </div>
      }
    </>}
  </div>;
}


const useReturnWindowStyles = makeBtStyles( theme => ({
  timeLeft: {
    fontWeight: 'bold'
  },
  pastWindow: {
    color: '#E73434'
  }
}));


const ReturnWindowMessage = ({ eligible_until_timestamp }:{ eligible_until_timestamp: string }) => {
  const classes = useReturnWindowStyles();

  const now = moment();
  const end = moment(eligible_until_timestamp);
  const pastWindow = now > end;
  const daysLeft = end.diff( now, 'days' ); // returns 0 if less than a day (negative if negative by _more_ than a day)
  const hoursLeft = end.diff( now, 'hours' ); // returns 0 if less than an hour (negative if negative by _more_ than an hour)

  const TimeLeft = ({ timeLeft }:{ timeLeft: React.ReactNode }) => <span className={classes.timeLeft}>{timeLeft}</span>;
  
  return <div className={pastWindow ? classes.pastWindow : null}>
    {(pastWindow) ? (
      'You are past your return window'
    ):( (hoursLeft === 0) ? (
      <>You have less than <TimeLeft timeLeft={'1 hour'} /> to return</>
    ):( (daysLeft === 0) ? (
      <FormattedMessage
        defaultMessage="You have <timeLeft>{hours, number} {hours, plural, one {hour} other {hours}}</timeLeft> left to return"
        values={{
          timeLeft: (chunks:any) => <TimeLeft timeLeft={chunks} />,
          hours: hoursLeft
        }}
      />
    ):(
      <FormattedMessage
        defaultMessage="You have <timeLeft>{days, number} {days, plural, one {day} other {days}}</timeLeft> left to return"
        values={{
          timeLeft: (chunks:any) => <TimeLeft timeLeft={chunks} />,
          days: daysLeft
        }}
      />
    )))}
  </div>;
};
